import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Spinner } from "@/components/ui/Spinner";
import {
  PublicOnlyRoute,
  RequireAuth,
  RequireAal2,
  RequireAdmin,
  RequireRole,
} from "./guards";

// ── Lazy imports ──────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const RecuperarPasswordPage = lazy(
  () => import("@/features/auth/pages/RecuperarPasswordPage"),
);
const ResetPasswordPage = lazy(
  () => import("@/features/auth/pages/ResetPasswordPage"),
);
const OnboardingPage = lazy(
  () => import("@/features/auth/pages/OnboardingPage"),
);
const AlterarPasswordPage = lazy(
  () => import("@/features/auth/pages/AlterarPasswordPage"),
);
const DashboardPage = lazy(
  () => import("@/features/auth/pages/DashboardPage"),
);
const DefinicoesSegurancaPage = lazy(
  () => import("@/features/auth/pages/DefinicoesSegurancaPage"),
);
const AdminUtilizadoresPage = lazy(
  () => import("@/features/admin/pages/AdminUtilizadoresPage"),
);
const AdminNovoUtilizadorPage = lazy(
  () => import("@/features/admin/pages/AdminNovoUtilizadorPage"),
);
const ReferenciacoesPage = lazy(
  () => import("@/features/referrals/pages/ReferenciacoesPage"),
);
const NovaReferenciacaoPage = lazy(
  () => import("@/features/referrals/pages/NovaReferenciacaoPage"),
);
const HistoricoDoentesPage = lazy(
  () => import("@/features/referrals/pages/HistoricoDoentesPage"),
);
const InternamentoPage = lazy(
  () => import("@/features/internamento/pages/InternamentoPage"),
);
const AvaliacaoSocialPage = lazy(
  () =>
    import("@/features/social-assessments/pages/AvaliacaoSocialPage"),
);
const HistoricoAvaliacaoSocialPage = lazy(
  () =>
    import(
      "@/features/social-assessments/pages/HistoricoAvaliacaoSocialPage"
    ),
);
const EscalaMedicaPage = lazy(
  () => import("@/features/gestao/pages/EscalaMedicaPage"),
);
const EscalaEnfermagemPage = lazy(
  () => import("@/features/gestao/pages/EscalaEnfermagemPage"),
);
const VerificacaoMalasPage = lazy(
  () => import("@/features/gestao/pages/VerificacaoMalasPage"),
);
const DefinicoesPage = lazy(
  () => import("@/features/settings/pages/DefinicoesPage"),
);
const AppLayout = lazy(() => import("@/layouts/AppLayout"));

// ── Fallback ──────────────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ── Root route ────────────────────────────────────────────────────────────────
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ── Public routes ─────────────────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <PublicOnlyRoute>
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    </PublicOnlyRoute>
  ),
});

const recuperarPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recuperar-password",
  component: () => (
    <PublicOnlyRoute>
      <SuspenseWrapper>
        <RecuperarPasswordPage />
      </SuspenseWrapper>
    </PublicOnlyRoute>
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: () => (
    // reset-password chega via magic link — tem sessão temporária, não é PublicOnly
    <SuspenseWrapper>
      <ResetPasswordPage />
    </SuspenseWrapper>
  ),
});

// ── Onboarding (requires auth, bypasses must_change_password check) ───────────
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: () => (
    <RequireAuth>
      <SuspenseWrapper>
        <OnboardingPage />
      </SuspenseWrapper>
    </RequireAuth>
  ),
});

// ── Autenticado — layout wrapper ──────────────────────────────────────────────
const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: () => (
    <RequireAuth>
      <SuspenseWrapper>
        <AppLayout />
      </SuspenseWrapper>
    </RequireAuth>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/dashboard",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <DashboardPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const alterarPasswordRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/alterar-password",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <AlterarPasswordPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const definicoesIndexRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/definicoes",
  component: () => (
    <RequireAdmin>
      <SuspenseWrapper>
        <DefinicoesPage />
      </SuspenseWrapper>
    </RequireAdmin>
  ),
});

const definicoesSegurancaRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/definicoes/seguranca",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <DefinicoesSegurancaPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const adminUtilizadoresRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/admin/utilizadores",
  component: () => (
    <RequireAdmin>
      <SuspenseWrapper>
        <AdminUtilizadoresPage />
      </SuspenseWrapper>
    </RequireAdmin>
  ),
});

const adminNovoUtilizadorRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/admin/utilizadores/novo",
  component: () => (
    <RequireAdmin>
      <SuspenseWrapper>
        <AdminNovoUtilizadorPage />
      </SuspenseWrapper>
    </RequireAdmin>
  ),
});

const referenciacoesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/referenciacoes",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <ReferenciacoesPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const novaReferenciacaoRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/referenciacoes/nova",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <NovaReferenciacaoPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const historicoDoentesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/historico-doentes",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <HistoricoDoentesPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const internamentoRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/internamento",
  component: () => (
    <RequireRole roles={["medico", "enfermeiro"]}>
      <SuspenseWrapper>
        <InternamentoPage />
      </SuspenseWrapper>
    </RequireRole>
  ),
});

// /avaliacao-social → redireciona para /avaliacao-social/por-avaliar
const avaliacaoSocialIndexRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/avaliacao-social",
  component: () => {
    window.location.replace("/avaliacao-social/por-avaliar");
    return null;
  },
});

const avaliacaoSocialPorAvaliarRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/avaliacao-social/por-avaliar",
  component: () => (
    <RequireRole roles={["assistente_social"]}>
      <SuspenseWrapper>
        <AvaliacaoSocialPage />
      </SuspenseWrapper>
    </RequireRole>
  ),
});

const avaliacaoSocialHistoricoRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/avaliacao-social/historico",
  component: () => (
    <RequireRole roles={["assistente_social"]}>
      <SuspenseWrapper>
        <HistoricoAvaliacaoSocialPage />
      </SuspenseWrapper>
    </RequireRole>
  ),
});

const escalaMedicaRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/gestao/escala-medica",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <EscalaMedicaPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const escalaEnfermagemRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/gestao/escala-enfermagem",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <EscalaEnfermagemPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

const verificacaoMalasRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: "/gestao/verificacao-malas",
  component: () => (
    <RequireAal2>
      <SuspenseWrapper>
        <VerificacaoMalasPage />
      </SuspenseWrapper>
    </RequireAal2>
  ),
});

// ── Index redirect ─────────────────────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => {
    // Handled by auth guards elsewhere — just navigate to dashboard
    window.location.replace("/dashboard");
    return null;
  },
});

// ── Router ────────────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  recuperarPasswordRoute,
  resetPasswordRoute,
  onboardingRoute,
  authenticatedLayoutRoute.addChildren([
    dashboardRoute,
    alterarPasswordRoute,
    definicoesIndexRoute,
    definicoesSegurancaRoute,
    adminUtilizadoresRoute,
    adminNovoUtilizadorRoute,
    referenciacoesRoute,
    novaReferenciacaoRoute,
    historicoDoentesRoute,
    internamentoRoute,
    avaliacaoSocialIndexRoute,
    avaliacaoSocialPorAvaliarRoute,
    avaliacaoSocialHistoricoRoute,
    escalaMedicaRoute,
    escalaEnfermagemRoute,
    verificacaoMalasRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
